import { Box, BoxProps, Heading, Text } from '@chakra-ui/react';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { useCallback, useEffect, useRef } from 'react';
import { useMeasure } from 'react-use';

export const TransferLoading = () => {
  const { t } = useTranslation('common');

  return (
    <Box pos="relative" overflow="hidden" w="full" maxW={rem(512)} h={rem(420)} p="space.32">
      <Heading fontSize="2xl" fontWeight="normal" mb="space.12">
        {t('transfer-modal.pending.title')}
      </Heading>
      <Text color="gray.6">{t('transfer-modal.pending.description')}</Text>
      <Pending
        position="absolute"
        left="50%"
        bottom={0}
        w={{ base: '100vw', sm: rem(700), md: rem(700) }}
        h={{ base: '100vw', sm: rem(700), md: rem(700) }}
        transform="translate(-50%, 72%)"
        transition="opacity .25s ease-in-out"
      />
    </Box>
  );
};

const CIRCLES_SIZES = [0.09829, 0.10256, 0.09829];

export const Pending = (boxProps: BoxProps) => {
  const [element, { height }] = useMeasure<HTMLDivElement>();

  const sizes = CIRCLES_SIZES.reduce<number[]>(
    (sizes, thickness, index) => (index ? [...sizes, sizes[index - 1] - thickness * 2] : [1]),
    [],
  );

  const holeSize = CIRCLES_SIZES.reduce((size, thickness) => size - thickness * 2, 1);

  const animate = true;

  return (
    <Box ref={element} aria-hidden position={'relative'} overflow="hidden" {...boxProps}>
      {sizes.map((size, index) => (
        <Shape
          key={index}
          width={`${size * 100}%`}
          height={`${size * 100}%`}
          position="absolute"
          top="50%"
          transform="translateY(-50%)"
          left={`${(height - height * size) * 0.5}px`}
          rotation={index * 38}
          animate={animate}
        />
      ))}

      <Shape
        color="gray.1"
        width={`${holeSize * 100}%`}
        height={`${holeSize * 100}%`}
        position="absolute"
        top="50%"
        left={`${(height - height * holeSize) * 0.5}px`}
        transform="translateY(-50%)"
      />
    </Box>
  );
};

const ROTATION_SPEED = 1.5; // degrees per animation-frame

type Props = BoxProps & { rotation?: number; animate?: boolean };

export const Shape = ({ rotation: startRotation, animate, color, ...boxProps }: Props) => {
  const element = useRef<HTMLDivElement>(null);
  const rotation = useRef(startRotation || 0);
  const animation = useRef<ReturnType<typeof requestAnimationFrame>>();

  const rotate = useCallback(() => {
    rotation.current = (rotation.current + ROTATION_SPEED) % 360;

    if (element.current) {
      element.current.style.backgroundImage = `conic-gradient(
        from ${rotation.current}deg at 50% 50%,
        #0D7EC4 0deg,
        #139EDD 60deg,
        #BBDBFF 114.38deg,
        #FEA514 174.38deg,
        #FF4539 210deg,
        #FF5544 247.5deg,
        #FF6A9A 292.5deg,
        #BBDBFF 317.5deg,
        #BBDBFF 327.5deg,
        #0D7EC4 360deg
      )`;
    }

    animation.current = requestAnimationFrame(rotate);
  }, [element, rotation]);

  useEffect(() => {
    if (!animate || color) return;
    rotate();
    return () => cancelAnimationFrame(animation.current!);
  }, [animate, rotate, startRotation, color]);

  return (
    <Box ref={element} borderRadius="full" overflow="hidden" background={color} {...boxProps} />
  );
};
